import React from 'react';
import { useHistory } from 'react-router-dom';
import { track } from '@amplitude/analytics-browser';
import {
  RocketIcon,
  StarIcon,
  TagIcon,
  TiendanubeIcon,
} from '@nimbus-ds/icons';
import { goTo } from '@tiendanube/nexo';
import nexo from 'App/nexoClient';
import { ADMIN_ROUTES, ROUTES } from 'App/Routes/constants';
import {
  useGetConcessionPaymentProviders,
  useInstallation,
  useNextActionHandler,
  useTransactionsQuery,
} from 'commons/hooks';
import { useTranslationWithPrefix } from 'commons/utils';
import {
  EmptyStateMexicoStatus,
  InstallationResponse,
  InstallationStatus,
  MessageCode,
  MessageSeverity,
  NextActionCode,
} from '../types';

interface EmptyStateMexicoContext {
  status: EmptyStateMexicoStatus;
  bullets: { icon: React.ReactNode; text: string }[];
  handleClick: () => void;
  isBtnEnabled: boolean;
  alertEnabled: boolean;
  title: string;
  subtitle: string;
  requestDate?: string;
  messageCode?: MessageCode;
  messageSeverity?: MessageSeverity;
  nextActionCode?: NextActionCode;
  deadline?: string;
}

export const useMexicoEmptyState = () => {
  const { t } = useTranslationWithPrefix('landing');

  const history = useHistory();

  const { data: installation, isLoading: isLoadingInstallation } =
    useInstallation();
  const {
    data: concessionPaymentProvidersData,
    isLoading: isLoadingConcessionPaymentProviders,
  } = useGetConcessionPaymentProviders();
  const { hasTransactions, isLoading: isLoadingTransactions } =
    useTransactionsQuery({ page: 1 });
  const { getHandlerForNextAction } = useNextActionHandler();

  const getEmptyStateMexico = () => {
    const handleClickActivate = () => {
      track('tiendanube_mx_pagonube_landing_activate_click');
      goTo(nexo, ADMIN_ROUTES.NUVEMPAGO_PAYMENT_METHODS);
    };

    const handleClickSuccess = () => {
      track('pagonube_active_empty_click');
      history.push(ROUTES.CONFIG);
    };

    const isPagoNubeEnabled = Boolean(concessionPaymentProvidersData?.enabled);

    const defaultBullets = [
      { icon: <TiendanubeIcon />, text: t('tiendaNubeIcon') },
      { icon: <RocketIcon />, text: t('rocketIcon') },
      { icon: <TagIcon />, text: t('tagIcon') },
      { icon: <StarIcon />, text: t('starIcon') },
    ];

    const handleClick = getHandlerForNextAction(installation?.nextAction?.code);

    const baseState = {
      bullets: defaultBullets,
      handleClick,
      title: t('title'),
      subtitle: t('subtitle'),
      alertEnabled: !!installation?.message,
      isBtnEnabled:
        installation?.status !== InstallationStatus.PROCESSING &&
        !installation?.nextAction,
      messageCode: installation?.message?.code,
      messageSeverity: installation?.message?.severity,
      nextActionCode: installation?.nextAction?.code,
      deadline: installation?.nextAction?.deadline,
    };

    if (!installation) {
      return {
        ...baseState,
        status: EmptyStateMexicoStatus.NotInstalled,
        handleClick: handleClickActivate,
        alertEnabled: false,
        isBtnEnabled: true,
      };
    }

    const status = getLegacyStatus(installation, isPagoNubeEnabled);

    // Stores not yet registered in commons come back as PENDING, not 404.
    // A pending next action keeps its own handler for the alert button.
    if (
      status === EmptyStateMexicoStatus.NotInstalled &&
      !installation.nextAction
    ) {
      return {
        ...baseState,
        status,
        handleClick: handleClickActivate,
      };
    }

    if (status === EmptyStateMexicoStatus.Success) {
      return {
        ...baseState,
        status,
        bullets: [
          { icon: <TiendanubeIcon />, text: t('success.tiendaNubeIcon') },
          { icon: <RocketIcon />, text: t('success.rocketIcon') },
          { icon: <TagIcon />, text: t('success.tagIcon') },
        ],
        title: t('success.title'),
        subtitle: t('success.subtitle'),
        handleClick: handleClickSuccess,
        isBtnEnabled: true,
      };
    }

    return {
      ...baseState,
      status,
    };
  };

  const getLegacyStatus = (
    installationData: InstallationResponse,
    isPagoNubeEnabled: boolean,
  ): EmptyStateMexicoStatus => {
    if (installationData.status === InstallationStatus.COMPLETED) {
      return isPagoNubeEnabled
        ? EmptyStateMexicoStatus.Success
        : EmptyStateMexicoStatus.UnabledToUse;
    }

    if (installationData.status === InstallationStatus.PROCESSING) {
      return EmptyStateMexicoStatus.Processing;
    }

    if (installationData.status === InstallationStatus.ACTION_REQUIRED) {
      return installationData.nextAction?.code ===
        NextActionCode.UPLOAD_DOCUMENTS
        ? EmptyStateMexicoStatus.ActionRequiredMissingDocs
        : EmptyStateMexicoStatus.ActionRequiredMissingData;
    }

    return EmptyStateMexicoStatus.NotInstalled;
  };

  const emptyState: EmptyStateMexicoContext = getEmptyStateMexico();
  const isLoading =
    isLoadingTransactions ||
    isLoadingInstallation ||
    isLoadingConcessionPaymentProviders;
  const shouldRedirectHome =
    installation?.status === InstallationStatus.COMPLETED && hasTransactions;

  return { emptyState, isLoading, shouldRedirectHome };
};
